import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Avatar,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import StorefrontIcon from '@mui/icons-material/Storefront';
import InstagramIcon from '@mui/icons-material/Instagram';
import PaymentsIcon from '@mui/icons-material/Payments';
import CategoryIcon from '@mui/icons-material/Category';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import CollectionsIcon from '@mui/icons-material/Collections';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import AddToQueueIcon from '@mui/icons-material/AddToQueue';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LanguageIcon from '@mui/icons-material/Language';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import WorkHistoryIcon from '@mui/icons-material/WorkHistory';
import { providerApi } from '../../services/api';
import { imageApi, videoApi } from '../../services/api';
import { normalizeImageUrl, normalizeVideoUrl } from '../../services/imageUtils';
import { useAuth } from '../../contexts/AuthContext';
import LocationPickerMap from '../LocationPickerMap';
import BusinessLocationPicker from '../BusinessLocationPicker';

// ── Section shell (mirrors the dashboard's border-based card style) ──────────
function Section({ title, action, children, sx }) {
  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: { xs: 2, sm: 3 },
        mb: 2.5,
        ...sx,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5, minHeight: 32 }}>
        <Typography
          variant="caption"
          sx={{
            color: 'text.disabled',
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            fontSize: '0.68rem',
          }}
        >
          {title}
        </Typography>
        {action}
      </Box>
      {children}
    </Paper>
  );
}

// ── Read-only info row used in view mode ─────────────────────────────────────
function InfoRow({ icon: Icon, label, value, source }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const color = theme.palette.primary.main;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
      <Avatar sx={{ bgcolor: alpha(color, 0.1), color, width: 38, height: 38, borderRadius: 1.5, flexShrink: 0 }}>
        <Icon sx={{ fontSize: 19 }} />
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="caption"
          sx={{ color: 'text.disabled', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', fontSize: '0.6rem', display: 'block' }}
        >
          {label}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          <Typography variant="body2" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>
            {value || t('profile.notSet')}
          </Typography>
          {source === 'account' && value ? (
            <Chip label={t('profile.fromAccount')} size="small" sx={{ height: 20, fontSize: '0.62rem' }} />
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}

const TWO_COL = { display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } };

const createFormData = (provider, currencyCode) => ({
  title: provider.title || '',
  about: provider.about || '',
  yearsOfExperience: provider.yearsOfExperience ?? '',
  publicEmail: provider.publicEmail || '',
  publicPhone: provider.publicPhone || '',
  publicWebsite: provider.publicWebsite || '',
  instagramUser: provider.instagramUser || '',
  businessCity: provider.businessCity || '',
  businessAddress: provider.businessAddress || '',
  businessLat: provider.businessLat ?? '',
  businessLng: provider.businessLng ?? '',
  businessCityId: provider.businessCityId ?? null,
  businessCountryId: provider.businessCountryId ?? null,
  timeZone: provider.timeZone || '',
});

/**
 * Business time zones offered in the picker. Deliberately short: the server validates against
 * the full IANA set, so this only has to cover where providers actually operate — a 600-entry
 * dropdown would make the common case harder, not easier.
 */
const SUPPORTED_TIME_ZONES = [
  'Asia/Jerusalem',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Madrid',
  'America/New_York',
  'America/Los_Angeles',
  'UTC',
];

const present = (value) => value !== null && value !== undefined && String(value).trim() !== '';

const isSafeWebsite = (value) => {
  if (!present(value)) return true;
  try {
    const parsed = new URL(/^https?:\/\//i.test(value) ? value : `https://${value}`);
    return ['http:', 'https:'].includes(parsed.protocol) && Boolean(parsed.hostname);
  } catch {
    return false;
  }
};

const ProviderProfile = ({ providerData, currencyCode = 'USD', onUpgradeClick }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const { refreshProviderData } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [error, setError] = useState('');
  // Fields the backend rejected as already published by another business profile
  // (409 CONTACT_IDENTIFIER_TAKEN). Keyed by DTO field name so the input shows the error itself —
  // a banner alone leaves the owner guessing which of three contact channels was refused.
  const [conflictFields, setConflictFields] = useState([]);
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState(() => createFormData(providerData, currencyCode));
  const [images, setImages] = useState(providerData.images || []);
  const [videos, setVideos] = useState(providerData.videos || []);
  // The business logo: a storage object name like the gallery images. `''` is a deliberate clear —
  // the backend treats null as "leave unchanged" and an empty string as "remove".
  const [logo, setLogo] = useState(providerData.logo || '');
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const handleEdit = () => {
    setIsEditing(true);
    setError('');
    setSuccess('');
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFormData(createFormData(providerData, currencyCode));
    setImages(providerData.images || []);
    setVideos(providerData.videos || []);
    setLogo(providerData.logo || '');
    setError('');
    setSuccess('');
  };

  const handleAddImage = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);
    setError('');

    try {
      // Upload image using imageApi
      const result = await imageApi.uploadImage(file);

      // Persist the storage object name, not an environment-specific absolute URL.
      // The dashboard expands it only for previews and the public API expands it
      // against the media origin of whichever environment serves the portal.
      if (!result.name) {
        throw new Error('Image upload did not return a storage object name');
      }
      setImages((currentImages) => [...currentImages, result.name]);
    } catch (error) {
      console.error('Error uploading image:', error);
      setError(t('profile.imageUploadFailed'));
    } finally {
      setUploading(false);
      // Reset the file input
      event.target.value = '';
    }
  };

  const handleRemoveImage = (index) => {
    setImages(images.filter((_, i) => i !== index));
  };

  const handleAddVideo = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadingVideo(true);
    setError('');
    try {
      const result = await videoApi.uploadVideo(file);
      if (!result.name) throw new Error('Video upload did not return a storage object name');
      setVideos((currentVideos) => [...currentVideos, result.name]);
    } catch (uploadError) {
      console.error('Error uploading video:', uploadError);
      setError(t('profile.videoUploadFailed'));
    } finally {
      setUploadingVideo(false);
      event.target.value = '';
    }
  };

  const handleRemoveVideo = (index) => {
    setVideos(videos.filter((_, i) => i !== index));
  };

  // Same path as a gallery photo — POST /images/upload runs Vision SafeSearch before storing.
  const handleAddLogo = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadingLogo(true);
    setError('');
    try {
      const result = await imageApi.uploadImage(file);
      if (!result.name) throw new Error('Image upload did not return a storage object name');
      setLogo(result.name);
    } catch (uploadError) {
      console.error('Error uploading logo:', uploadError);
      setError(t('profile.imageUploadFailed'));
    } finally {
      setUploadingLogo(false);
      event.target.value = '';
    }
  };

  const handleRemoveLogo = () => setLogo('');

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  const validateForm = () => {
    const errors = [];

    if (!present(formData.title)) {
      errors.push(t('profile.errNameRequired'));
    }

    // City is mandatory: it seeds the owner's location and is what makes the mobile
    // marketplace tab (gated on the user's city) appear. Country is fixed to Israel.
    if (!present(formData.businessCity)) {
      errors.push(t('profile.errCityRequired'));
    }

    if (formData.publicEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.publicEmail)) {
      errors.push(t('profile.errPublicEmail'));
    }

    if (!isSafeWebsite(formData.publicWebsite)) {
      errors.push(t('profile.errWebsite'));
    }

    if (present(formData.yearsOfExperience)) {
      const years = Number(formData.yearsOfExperience);
      if (!Number.isInteger(years) || years < 0 || years > 100) {
        errors.push(t('profile.errYears'));
      }
    }

    const hasLat = present(formData.businessLat);
    const hasLng = present(formData.businessLng);
    if (hasLat !== hasLng) {
      errors.push(t('profile.errLatLngPair'));
    } else if (hasLat && hasLng) {
      const lat = Number(formData.businessLat);
      const lng = Number(formData.businessLng);
      if (!Number.isFinite(lat) || lat < -90 || lat > 90) errors.push(t('profile.errLat'));
      if (!Number.isFinite(lng) || lng < -180 || lng > 180) errors.push(t('profile.errLng'));
    }

    return errors;
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError('');
    setSuccess('');

    // Validate form
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setError(validationErrors.join('. '));
      setIsSaving(false);
      return;
    }

    try {
      // The PUT is a full overwrite, so merge our edited fields onto the LATEST
      // server truth — never a stale local copy. This prevents clobbering fields
      // this screen doesn't own (e.g. `visible`, `providerState`, `services`).
      const latest = (await refreshProviderData()) || providerData;

      // Prepare updated provider data with proper type conversion
      const updatedProviderData = {
        ...latest,
        ...formData,
        yearsOfExperience: present(formData.yearsOfExperience) ? Number(formData.yearsOfExperience) : null,
        businessLat: present(formData.businessLat) ? Number(formData.businessLat) : null,
        businessLng: present(formData.businessLng) ? Number(formData.businessLng) : null,
        businessCityId: formData.businessCityId ?? null,
        businessCountryId: formData.businessCountryId ?? null,
        businessAddress: formData.businessAddress || null,
        // Booking contract §5: required before this provider can accept paid bookings — quote
        // acceptance is refused without it, because every deadline is calendar math in it.
        timeZone: formData.timeZone,
        images,
        videos,
        logo,
      };
      await providerApi.updateProvider(providerData.id, updatedProviderData);
      // Refresh shared provider data in place (no full reload, keeps current tab)
      const refreshed = await refreshProviderData();
      setFormData(createFormData(refreshed || updatedProviderData, currencyCode));
      setSuccess(t('profile.updated'));
      setIsEditing(false);
      setIsSaving(false);
      setConflictFields([]);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      const data = err.response?.data;
      // Stay in edit mode with the values kept: the owner has to change the refused channel,
      // and re-typing the whole form would be the real punishment here.
      setConflictFields(data?.error === 'CONTACT_IDENTIFIER_TAKEN' ? (data.fields || []) : []);
      setError(data?.message || t('profile.updateFailed'));
      setIsSaving(false);
    }
  };

  const conflictOn = (field) => conflictFields.includes(field);
  const CONTACT_TAKEN = t('profile.contactTaken');

  // Header mark: the logo, else the first gallery photo, else the storefront icon.
  const coverImage = logo ? normalizeImageUrl(logo) : images && images.length > 0 ? normalizeImageUrl(images[0]) : null;
  const account = providerData.user || {};
  const accountFallbacks = {
    email: account.emailVerified ? account.email : null,
    phone: account.phone || null,
    city: account.city || null,
  };
  const effectivePublic = {
    email: present(formData.publicEmail) ? formData.publicEmail : accountFallbacks.email,
    phone: present(formData.publicPhone) ? formData.publicPhone : accountFallbacks.phone,
    city: present(formData.businessCity) ? formData.businessCity : accountFallbacks.city,
    website: present(formData.publicWebsite) ? formData.publicWebsite : null,
    instagram: present(formData.instagramUser) ? formData.instagramUser : null,
  };
  const sourceOf = (businessValue, fallbackValue) => !present(businessValue) && present(fallbackValue) ? 'account' : 'business';

  return (
    <Box>
      {/* ── Profile header ──────────────────────────────────────────────── */}
      <Paper
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
          mb: 2.5,
        }}
      >
        {/* Cover band */}
        <Box
          sx={{
            height: 96,
            bgcolor: 'background.paper',
          }}
        />
        <Box sx={{ px: { xs: 2, sm: 3 }, pb: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, mt: -5, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
              <Avatar
                src={coverImage || undefined}
                sx={{
                  width: 88,
                  height: 88,
                  border: '4px solid',
                  borderColor: 'background.paper',
                  bgcolor: alpha(primary, 0.15),
                  color: primary,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                }}
              >
                {!coverImage && <StorefrontIcon sx={{ fontSize: 40 }} />}
              </Avatar>
              {isEditing && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      variant="outlined"
                      component="label"
                      size="small"
                      startIcon={uploadingLogo ? <CircularProgress size={16} /> : <AddPhotoAlternateIcon />}
                      disabled={uploadingLogo}
                    >
                      {uploadingLogo ? t('profile.uploading') : t('profile.logoUpload')}
                      <input type="file" hidden accept="image/*" onChange={handleAddLogo} disabled={uploadingLogo} />
                    </Button>
                    {logo && (
                      <Button
                        variant="text"
                        size="small"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={handleRemoveLogo}
                        disabled={uploadingLogo}
                      >
                        {t('profile.logoRemove')}
                      </Button>
                    )}
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('profile.logoHint')}
                  </Typography>
                </Box>
              )}
            </Box>
            <Box sx={{ flex: 1, minWidth: 200, pb: 0.5 }}>
              <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                {formData.title || t('overview.providerFallback')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 0.75, flexWrap: 'wrap' }}>
                <Chip
                  size="small"
                  icon={<CategoryIcon sx={{ fontSize: 15 }} />}
                  label={t('nav.type' + providerData.providerType, { defaultValue: providerData.providerType })}
                  sx={{ bgcolor: alpha(primary, 0.1), color: primary, fontWeight: 600 }}
                />
                {providerData.isProPlan ? (
                  <Chip
                    size="small"
                    label={t('plan.pro')}
                    color="warning"
                    sx={{ fontWeight: 600, cursor: onUpgradeClick ? 'pointer' : 'default' }}
                    onClick={onUpgradeClick}
                  />
                ) : providerData.providerType !== 'STORE' && (
                  <Chip
                    size="small"
                    label={t('plan.free')}
                    sx={{ fontWeight: 600, cursor: onUpgradeClick ? 'pointer' : 'default' }}
                    onClick={onUpgradeClick}
                  />
                )}
              </Box>
            </Box>
            {!isEditing && (
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={handleEdit}
                sx={{ pb: 0.5, alignSelf: 'center' }}
              >
                {t('profile.editButton')}
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2.5 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2.5 }}>{success}</Alert>}

      {isEditing ? (
        /* ── EDIT MODE ──────────────────────────────────────────────────── */
        <>
          <Section title={t('profile.basicInfo')}>
            <Box sx={TWO_COL}>
              <TextField
                label={t('profile.businessName')}
                fullWidth
                value={formData.title}
                onChange={handleChange('title')}
              />
              <TextField
                label={t('profile.yearsOfExperience')}
                fullWidth
                type="number"
                value={formData.yearsOfExperience}
                onChange={handleChange('yearsOfExperience')}
                inputProps={{ min: 0, max: 100, step: 1 }}
                InputProps={{ startAdornment: <WorkHistoryIcon sx={{ mr: 1, color: 'text.disabled' }} fontSize="small" /> }}
              />
              <TextField
                label={t('profile.about')}
                fullWidth
                multiline
                rows={4}
                placeholder={t('profile.aboutPlaceholder')}
                value={formData.about}
                onChange={handleChange('about')}
                sx={{ gridColumn: { md: '1 / -1' } }}
              />
            </Box>
          </Section>

          <Section
            title={t('profile.publicProfile')}
            action={present(account.lat) && present(account.lng) ? (
              <Button
                size="small"
                variant="outlined"
                startIcon={<MyLocationIcon />}
                onClick={() => setFormData((current) => ({
                  ...current,
                  businessLat: account.lat,
                  businessLng: account.lng,
                  businessCity: current.businessCity || account.city || '',
                }))}
              >
                {t('profile.useAccountLocation')}
              </Button>
            ) : null}
          >
            <Alert severity="info" sx={{ mb: 2.5 }}>
              {t('profile.publicProfileHint')}
            </Alert>
            <Box sx={TWO_COL}>
              <TextField
                label={t('profile.publicEmail')}
                fullWidth
                type="email"
                value={formData.publicEmail}
                onChange={handleChange('publicEmail')}
                placeholder={accountFallbacks.email || 'contact@yourbusiness.com'}
                error={conflictOn('publicEmail')}
                helperText={conflictOn('publicEmail')
                  ? CONTACT_TAKEN
                  : !present(formData.publicEmail) && accountFallbacks.email
                    ? t('profile.usingAccountEmail', { email: accountFallbacks.email })
                    : t('profile.publicEmailHelper')}
                InputProps={{ startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.disabled' }} fontSize="small" /> }}
              />
              <TextField
                label={t('profile.publicPhone')}
                fullWidth
                value={formData.publicPhone}
                onChange={handleChange('publicPhone')}
                placeholder={accountFallbacks.phone || '050-000-0000'}
                error={conflictOn('publicPhone')}
                helperText={conflictOn('publicPhone')
                  ? CONTACT_TAKEN
                  : !present(formData.publicPhone) && accountFallbacks.phone
                    ? t('profile.usingAccountPhone', { phone: accountFallbacks.phone })
                    : t('profile.publicPhoneHelper')}
                InputProps={{ startAdornment: <PhoneIcon sx={{ mr: 1, color: 'text.disabled' }} fontSize="small" /> }}
              />
              <TextField
                label={t('profile.website')}
                fullWidth
                value={formData.publicWebsite}
                onChange={handleChange('publicWebsite')}
                placeholder="https://yourbusiness.com"
                helperText={t('profile.websiteHelper')}
                InputProps={{ startAdornment: <LanguageIcon sx={{ mr: 1, color: 'text.disabled' }} fontSize="small" /> }}
              />
              <TextField
                label={t('profile.instagram')}
                fullWidth
                value={formData.instagramUser}
                onChange={handleChange('instagramUser')}
                placeholder="yourbusiness"
                error={conflictOn('instagramUser')}
                helperText={conflictOn('instagramUser') ? CONTACT_TAKEN : undefined}
                InputProps={{ startAdornment: <InstagramIcon sx={{ mr: 1, color: 'text.disabled' }} fontSize="small" /> }}
              />
              <BusinessLocationPicker
                cityName={formData.businessCity}
                accountFallbackCity={accountFallbacks.city}
                onCityChange={(name, lat, lng, cityId, countryId) =>
                  setFormData((f) => ({
                    ...f,
                    businessCity: name,
                    businessCityId: cityId ?? null,
                    businessCountryId: countryId ?? null,
                    ...(lat != null && lng != null ? { businessLat: lat, businessLng: lng } : {}),
                  }))
                }
              />
              <TextField
                label={t('profile.businessAddress')}
                fullWidth
                value={formData.businessAddress}
                onChange={handleChange('businessAddress')}
                placeholder="123 Main St, Floor 2"
                helperText={t('profile.addressHelper')}
                InputProps={{ startAdornment: <LocationOnIcon sx={{ mr: 1, color: 'text.disabled' }} fontSize="small" /> }}
                sx={{ gridColumn: { md: '1 / -1' } }}
              />
              <TextField
                label={t('profile.latitude')}
                fullWidth
                type="number"
                value={formData.businessLat}
                onChange={handleChange('businessLat')}
                inputProps={{ min: -90, max: 90, step: 'any' }}
                helperText={t('profile.latHelper')}
              />
              <TextField
                label={t('profile.longitude')}
                fullWidth
                type="number"
                value={formData.businessLng}
                onChange={handleChange('businessLng')}
                inputProps={{ min: -180, max: 180, step: 'any' }}
                helperText={t('profile.lngHelper')}
              />
              <Box sx={{ gridColumn: { md: '1 / -1' }, mt: 1 }}>
                <LocationPickerMap
                  latitude={formData.businessLat}
                  longitude={formData.businessLng}
                  onLocationChange={(lat, lng) =>
                    setFormData((f) => ({ ...f, businessLat: lat, businessLng: lng }))
                  }
                />
              </Box>
            </Box>
          </Section>

          <Section title={t('profile.paymentsAccount')}>
            <Box sx={TWO_COL}>
              <TextField
                label={t('profile.providerType')}
                fullWidth
                value={providerData.providerType}
                disabled
              />
              <TextField
                select
                label={t('profile.timeZone')}
                fullWidth
                value={formData.timeZone}
                onChange={handleChange('timeZone')}
                helperText={
                  formData.timeZone ? t('profile.timeZoneHelper') : t('profile.timeZoneRequired')
                }
                error={!formData.timeZone}
                SelectProps={{ native: true }}
                InputLabelProps={{ shrink: true }}
              >
                <option value="">{t('profile.timeZonePlaceholder')}</option>
                {SUPPORTED_TIME_ZONES.map((zone) => (
                  <option key={zone} value={zone}>{zone}</option>
                ))}
              </TextField>
            </Box>
          </Section>

          <Section
            title={t('profile.imagesTitle')}
            action={
              <Button
                variant="outlined"
                component="label"
                size="small"
                startIcon={uploading ? <CircularProgress size={16} /> : <AddPhotoAlternateIcon />}
                disabled={uploading}
              >
                {uploading ? t('profile.uploading') : t('profile.addImage')}
                <input type="file" hidden accept="image/*" onChange={handleAddImage} disabled={uploading} />
              </Button>
            }
          >
            {images.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4, color: 'text.disabled' }}>
                <CollectionsIcon sx={{ fontSize: 40, opacity: 0.5, mb: 1 }} />
                <Typography variant="body2">{t('profile.noImages')}</Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))' }}>
                {images.map((image, index) => (
                  <Box
                    key={index}
                    sx={{
                      position: 'relative',
                      aspectRatio: '1 / 1',
                      borderRadius: 2,
                      overflow: 'hidden',
                      border: '1px solid',
                      borderColor: 'divider',
                      '&:hover .img-overlay': { opacity: 1 },
                    }}
                  >
                    <img
                      src={normalizeImageUrl(image)}
                      alt={`Provider ${index + 1}`}
                      loading="lazy"
                      style={{ objectFit: 'cover', width: '100%', height: '100%', display: 'block' }}
                    />
                    <Box
                      className="img-overlay"
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        bgcolor: 'rgba(0,0,0,0.35)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: 0,
                        transition: 'opacity 0.2s',
                      }}
                    >
                      <Tooltip title={t('profile.removeImage')}>
                        <IconButton
                          onClick={() => handleRemoveImage(index)}
                          sx={{ bgcolor: 'rgba(255,255,255,0.92)', '&:hover': { bgcolor: '#fff' } }}
                          size="small"
                        >
                          <DeleteIcon fontSize="small" color="error" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Section>

          <Section
            title={t('profile.videosTitle')}
            action={
              <Button
                variant="outlined"
                component="label"
                size="small"
                startIcon={uploadingVideo ? <CircularProgress size={16} /> : <AddToQueueIcon />}
                disabled={uploadingVideo}
              >
                {uploadingVideo ? t('profile.uploading') : t('profile.addVideo')}
                <input type="file" hidden accept="video/*" onChange={handleAddVideo} disabled={uploadingVideo} />
              </Button>
            }
          >
            {videos.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4, color: 'text.disabled' }}>
                <VideoLibraryIcon sx={{ fontSize: 42, opacity: 0.45, mb: 1 }} />
                <Typography variant="body2">{t('profile.videosEmptyTitle')}</Typography>
                <Typography variant="caption">{t('profile.videosEmptyBody')}</Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))' }}>
                {videos.map((video, index) => (
                  <Box
                    key={`${video}-${index}`}
                    sx={{
                      position: 'relative',
                      aspectRatio: '16 / 10',
                      borderRadius: 2,
                      overflow: 'hidden',
                      bgcolor: 'common.black',
                      border: '1px solid',
                      borderColor: 'divider',
                      '&:hover .video-overlay': { opacity: 1 },
                    }}
                  >
                    <video
                      src={normalizeVideoUrl(video)}
                      controls
                      preload="metadata"
                      playsInline
                      style={{ objectFit: 'cover', width: '100%', height: '100%', display: 'block' }}
                    />
                    <Box
                      className="video-overlay"
                      sx={{ position: 'absolute', top: 8, right: 8, opacity: 0, transition: 'opacity 0.2s' }}
                    >
                      <Tooltip title={t('profile.removeVideo')}>
                        <IconButton
                          onClick={() => handleRemoveVideo(index)}
                          sx={{ bgcolor: 'rgba(255,255,255,0.94)', '&:hover': { bgcolor: 'common.white' } }}
                          size="small"
                        >
                          <DeleteIcon fontSize="small" color="error" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Section>

          <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
            <Button variant="outlined" startIcon={<CancelIcon />} onClick={handleCancel} disabled={isSaving}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="contained"
              startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              onClick={handleSave}
              disabled={isSaving}
            >
              {t('profile.saveButton')}
            </Button>
          </Box>
        </>
      ) : (
        /* ── VIEW MODE ──────────────────────────────────────────────────── */
        <>
          <Section title={t('profile.about')}>
            <Typography variant="body2" sx={{ color: formData.about ? 'text.primary' : 'text.disabled', whiteSpace: 'pre-line', lineHeight: 1.7 }}>
              {formData.about || t('profile.aboutEmpty')}
            </Typography>
          </Section>

          <Section title={t('profile.publicProfile')}>
            <Box sx={TWO_COL}>
              <InfoRow icon={CategoryIcon} label={t('profile.providerType')} value={t('nav.type' + providerData.providerType, { defaultValue: providerData.providerType })} />
              <InfoRow icon={WorkHistoryIcon} label={t('profile.experience')} value={present(formData.yearsOfExperience) ? t('profile.yearsValue', { count: Number(formData.yearsOfExperience) }) : null} />
              <InfoRow icon={EmailIcon} label={t('profile.publicEmail')} value={effectivePublic.email} source={sourceOf(formData.publicEmail, accountFallbacks.email)} />
              <InfoRow icon={PhoneIcon} label={t('profile.publicPhone')} value={effectivePublic.phone} source={sourceOf(formData.publicPhone, accountFallbacks.phone)} />
              <InfoRow icon={LocationOnIcon} label={t('profile.businessCity')} value={effectivePublic.city} source={sourceOf(formData.businessCity, accountFallbacks.city)} />
              <InfoRow icon={LocationOnIcon} label={t('profile.businessAddress')} value={formData.businessAddress || null} />
              <InfoRow icon={MyLocationIcon} label={t('profile.mapCoordinates')} value={present(formData.businessLat) && present(formData.businessLng) ? `${formData.businessLat}, ${formData.businessLng}` : null} />
              <InfoRow icon={LanguageIcon} label={t('profile.website')} value={effectivePublic.website} />
              <InfoRow icon={InstagramIcon} label={t('profile.instagram')} value={effectivePublic.instagram ? `@${effectivePublic.instagram.replace(/^@/, '')}` : null} />
            </Box>
          </Section>

          <Section title={t('profile.paymentsAccount')}>
            <Box sx={TWO_COL}>
              <InfoRow icon={PaymentsIcon} label={t('profile.paypalStatus')} value={providerData.paypalOnboardingStatus === 'COMPLETED' ? t('payments.connected') : t('payments.notConnected')} />
              {providerData.providerType !== 'STORE' && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
                  <Avatar
                    sx={{
                      bgcolor: alpha(providerData.isProPlan ? '#F59E0B' : primary, 0.1),
                      color: providerData.isProPlan ? '#F59E0B' : primary,
                      width: 38, height: 38, borderRadius: 1.5, flexShrink: 0,
                    }}
                  >
                    <WorkspacePremiumIcon sx={{ fontSize: 19 }} />
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.disabled', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', fontSize: '0.6rem', display: 'block' }}
                    >
                      {t('profile.planStatus')}
                    </Typography>
                    {providerData.isProPlan ? (
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: '#F59E0B', cursor: onUpgradeClick ? 'pointer' : 'default', '&:hover': onUpgradeClick ? { opacity: 0.75 } : {} }}
                        onClick={onUpgradeClick}
                      >
                        {t('plan.pro')}
                      </Typography>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {t('plan.free')}
                        </Typography>
                        {onUpgradeClick && (
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: 'primary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                            onClick={onUpgradeClick}
                          >
                            {t('profile.becomePro')}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          </Section>

          <Section title={images.length ? `${t('profile.imagesTitle')} · ${images.length}` : t('profile.imagesTitle')}>
            {images.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4, color: 'text.disabled' }}>
                <CollectionsIcon sx={{ fontSize: 40, opacity: 0.5, mb: 1 }} />
                <Typography variant="body2">{t('profile.noImages')}</Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' }}>
                {images.map((image, index) => (
                  <Box
                    key={index}
                    sx={{
                      aspectRatio: '1 / 1',
                      borderRadius: 2,
                      overflow: 'hidden',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <img
                      src={normalizeImageUrl(image)}
                      alt={`Provider ${index + 1}`}
                      loading="lazy"
                      style={{ objectFit: 'cover', width: '100%', height: '100%', display: 'block' }}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </Section>

          <Section title={videos.length ? `${t('profile.videosTitle')} · ${videos.length}` : t('profile.videosTitle')}>
            {videos.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4, color: 'text.disabled' }}>
                <VideoLibraryIcon sx={{ fontSize: 42, opacity: 0.45, mb: 1 }} />
                <Typography variant="body2">{t('profile.noVideos')}</Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))' }}>
                {videos.map((video, index) => (
                  <Box
                    key={`${video}-${index}`}
                    sx={{ aspectRatio: '16 / 10', borderRadius: 2, overflow: 'hidden', bgcolor: 'common.black', border: '1px solid', borderColor: 'divider' }}
                  >
                    <video
                      src={normalizeVideoUrl(video)}
                      controls
                      preload="metadata"
                      playsInline
                      style={{ objectFit: 'cover', width: '100%', height: '100%', display: 'block' }}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </Section>
        </>
      )}
    </Box>
  );
};

export default ProviderProfile;
